// src/components/MuralEvaluationPopup.js
//
// Modal disparada quando o autor arquiva manualmente um item do mural (Fase C.5).
// Persiste em `muralEvaluations` 3 perguntas Sim/Não + comentário livre.
// Após salvar, chama onConfirm para que o chamador finalize o arquivamento.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import Button from './Button/Button';
import H3 from './H3/H3';
import closeBtnImage from '../assets/btn-close.png';

const QUESTION_KEYS = ['fulfilled', 'viaPlatform', 'recommend'];

const MuralEvaluationPopup = ({ item, evaluatorId, onClose, onConfirm }) => {
  const { t } = useTranslation('mural');
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAnswer = (key, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      // A pergunta (b) só faz sentido se (a) for "Sim". Se (a) muda para "Não",
      // a resposta de (b) é descartada.
      if (key === 'fulfilled' && value !== true) {
        delete next.viaPlatform;
      }
      return next;
    });
  };

  // (a) e (c) são sempre obrigatórias; (b) só quando (a) === Sim.
  const requiredKeys = answers.fulfilled === true
    ? ['fulfilled', 'viaPlatform', 'recommend']
    : ['fulfilled', 'recommend'];
  const allAnswered = requiredKeys.every((key) => typeof answers[key] === 'boolean');

  const submitEvaluation = async () => {
    if (!allAnswered) {
      setError(t('evaluation.errorAnswerAll'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        itemId: item?.id || null,
        itemTitle: item?.title || '',
        itemType: item?.type || '',
        itemCategory: item?.category || '',
        evaluatorId: evaluatorId || null,
        answers,
        comment: comment.trim(),
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'muralEvaluations'), payload);
      if (onConfirm) await onConfirm(payload);
    } catch (err) {
      console.error('Erro ao registrar avaliação:', err);
      setError(t('evaluation.errorGeneric'));
    } finally {
      setSaving(false);
    }
  };

  const skipEvaluation = async () => {
    setSaving(true);
    try {
      if (onConfirm) await onConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex justify-center items-center notranslate">
      <div className="bg-white rounded-xl p-8 max-w-lg w-11/12 relative">
        <button
          type="button"
          className="absolute -top-3.5 -right-3.5 w-8 h-8 bg-transparent border-none cursor-pointer p-0"
          onClick={onClose}
          aria-label="Fechar"
        >
          <img src={closeBtnImage} alt="Fechar" />
        </button>
        <H3 text={t('evaluation.title')} textColor="purple" />
        <p className="text-sm text-gray-600 -mt-3 mb-4">{t('evaluation.subtitle')}</p>

        <div className="space-y-3 mb-4">
          {QUESTION_KEYS.map((key) => {
            // (b) só aparece quando (a) foi respondida com "Sim".
            if (key === 'viaPlatform' && answers.fulfilled !== true) return null;
            return (
            <div key={key} className="flex items-center justify-between gap-2">
              <span className="text-sm text-gray-800">{t(`evaluation.questions.${key}`)}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleAnswer(key, true)}
                  className={`px-3 py-1 rounded border text-sm ${
                    answers[key] === true
                      ? 'bg-purple-refugia text-white border-purple-refugia'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {t('common:common.yes')}
                </button>
                <button
                  type="button"
                  onClick={() => handleAnswer(key, false)}
                  className={`px-3 py-1 rounded border text-sm ${
                    answers[key] === false
                      ? 'bg-purple-refugia text-white border-purple-refugia'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {t('common:common.no')}
                </button>
              </div>
            </div>
            );
          })}
        </div>

        <label className="block text-sm text-gray-700 mb-1">{t('evaluation.commentLabel')}</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={400}
          className="w-full h-24 p-2 border border-gray-300 rounded-md"
          placeholder={t('evaluation.commentPlaceholder')}
        />
        <p className="text-xs text-gray-500 text-right">{comment.length}/400</p>

        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

        <div className="flex justify-between items-center mt-4">
          <button
            type="button"
            onClick={skipEvaluation}
            disabled={saving}
            className="text-sm text-gray-600 underline"
          >
            {t('evaluation.skip')}
          </button>
          <Button
            text={saving ? t('evaluation.submitting') : t('evaluation.submit')}
            variant="transparent"
            onClick={submitEvaluation}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
};

export default MuralEvaluationPopup;
